import { createClient } from "@connectrpc/connect";
import type { Polygon, Position } from "geojson";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import {
  ClienteFincaWizardService,
  GuardarResponse,
  LoteTemporal,
  RegistroTemporal,
} from "../../../../generated/grpc/terceros_finca_wizard_pb";
import {
  lineStringToWkb,
  pointToWkb,
  polygonToWkb,
  wkbToPoint,
  wkbToPolygon,
} from "../../../../utils/wkbUtils";
import { grpcFincaLoteDatabase } from "../../database/GrpcFincaLoteDatabase";
import { fx_createLoteGrpcService } from "../../services/LoteGrpcService";
import {
  RegistroFincaBorrador,
  RegistroFincaBorradorLote,
  RegistroTemporalLeido,
} from "../response/RegistroFincaBorrador";
import { mapFincaWizardGrpcError } from "./mapFincaWizardGrpcError";

/** Valores de `fuente` en el servidor (`Constantes.RegistroFinca.Fuente`)
 *  para cada `aoiOrigen` del borrador. */
const FUENTE_POR_ORIGEN = {
  draw: "Dibujado sobre mapa",
  kml: "Archivo importado",
} as const;

/** Lote a insertar: sin id (lo asigna el servidor). */
export type LoteTempNuevo = Omit<RegistroFincaBorradorLote, "id">;

/** Asignación del paso 5 de un lote. */
export type AsignacionLoteTemp = {
  isAreaProductiva: boolean | null;
  cultivoId: string | null;
  coberturaId: string | null;
};

/** Cambio sobre un lote ya guardado: nombre, asignación, o ambos. */
export type ActualizacionLoteTemp = {
  id: string;
  nombreLote?: string;
  asignacion?: AsignacionLoteTemp;
};

/** Guardado progresivo del wizard de registro de finca en el esquema
 *  temporal del servidor (`registro_finca`, rpc de `ClienteFincaWizardService`).
 *  Es la ÚNICA fuente de los datos del registro: cada paso lee de acá lo que
 *  necesita (`leerAsync`) y guarda acá lo que el usuario confirma; nada se
 *  arrastra en memoria de un paso a otro. Nada llega a las fincas reales
 *  hasta `finalizarAsync`, que el servidor resuelve leyendo el temporal y
 *  solo si el registro está completo.
 *
 *  Los errores con código propio (`REGISTRO_INCOMPLETO`, `NOMBRE_DUPLICADO`,
 *  `LOTE_SOLAPADO`, `GEOMETRIA_SOLAPADA`…) llegan con el shape JSON que ya
 *  leen `parseWizardError`/`parseWizardErrorMessage`
 *  (`mapFincaWizardGrpcError`). */
export class RegistroFincaTempGrpcService {
  private _client() {
    return createClient(ClienteFincaWizardService, grpcTransportProvider.get());
  }

  /** El registro en curso del cliente, o `null` si no tiene. */
  async obtenerEnCursoAsync(
    clienteId: string,
  ): Promise<RegistroTemporalLeido | null> {
    try {
      const response = await this._client().obtenerRegistroEnCurso({});
      return response.registro
        ? fx_leerRegistroTemporal(response.registro, clienteId)
        : null;
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Abre (o retoma) el registro temporal para EDITAR una finca ya completa:
   *  el servidor lo siembra con el área y los lotes que la finca tiene hoy y lo
   *  deja en `ultimoPaso = 3`, así el wizard abre en el paso 4.
   *
   *  Es idempotente: llamarlo de nuevo sobre la misma finca devuelve el
   *  registro que ya estaba, con los cortes que el usuario haya hecho. */
  async iniciarEdicionAsync(
    fincaId: string,
    clienteId: string,
  ): Promise<RegistroTemporalLeido> {
    try {
      const registro = await this._client().iniciarEdicionFinca({ fincaId });
      return fx_leerRegistroTemporal(registro, clienteId);
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Lee del servidor el registro del borrador (su área y sus lotes). Falla
   *  si ese registro ya no está en curso (se finalizó o se descartó en otra
   *  pestaña). */
  async leerAsync(borrador: RegistroFincaBorrador): Promise<RegistroTemporalLeido> {
    const leido = await this.obtenerEnCursoAsync(borrador.clienteId);
    if (!leido || leido.borrador.registroId !== borrador.registroId) {
      throw new Error("El registro de la finca ya no está en curso. Cierre el registro y vuelva a abrirlo.");
    }
    return leido;
  }

  /** Paso 1: crea el registro (sin `registroId`) o actualiza nombre/tipo.
   *  Devuelve la identidad del registro como quedó en el servidor. */
  async guardarDatosBasicosAsync(
    borrador: RegistroFincaBorrador,
  ): Promise<RegistroFincaBorrador> {
    try {
      const registro = await this._client().guardarDatosBasicos({
        registroId: borrador.registroId ?? undefined,
        fincaId: borrador.fincaIdACompletar ?? undefined,
        nombre: borrador.nombre ?? "",
        tipo: borrador.tipo ?? 0,
      });
      return fx_leerRegistroTemporal(registro, borrador.clienteId).borrador;
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Garantiza que el borrador tenga registro en el servidor y devuelve su
   *  id. Hace falta en el modo completar, que salta el paso 1 (que es quien
   *  normalmente crea el registro): lo crea con la finca a completar. */
  async asegurarRegistroAsync(borrador: RegistroFincaBorrador): Promise<string> {
    if (borrador.registroId) return borrador.registroId;
    const registro = await this.guardarDatosBasicosAsync(borrador);
    borrador.registroId = registro.registroId;
    borrador.ultimoPaso = registro.ultimoPaso;
    return registro.registroId!;
  }

  /** Paso 2, camino "Cargar archivo KML": el SERVIDOR lee el archivo ya
   *  subido a Azure Blob (`StorageService.upload`, SAS de escritura) y hace
   *  todo el procesamiento que antes hacía el navegador — parsear el KML/KMZ,
   *  abrir los multipolígonos en polígonos independientes, tomar el nombre
   *  del Placemark, redondear a 7 decimales, depurar y agrupar en padres.
   *
   *  **Por qué se movió.** El parseo es sincrónico (bloqueaba la pantalla) y
   *  la depuración compara todos los polígonos contra todos: con un archivo
   *  grande la app quedaba congelada varios segundos incluso con el Web
   *  Worker que había para eso.
   *
   *  Deja guardados en el registro la fuente, el archivo y los **lotes** del
   *  archivo. Lo que NO guarda es el área: devuelve los polígonos padre y
   *  quien llama los une con `fx_calcularPerimetroGlobal` (que sigue siendo
   *  del cliente, con sus puentes entre bloques) y la guarda con
   *  `guardarAoiAsync`, igual que cuando el usuario dibuja a mano. Así los
   *  pasos 3 y 4 reciben exactamente lo mismo que antes. */
  async importarArchivoKmlAsync(
    registroId: string,
    blobUrl: string,
    nombreArchivo: string,
  ): Promise<{
    /** Los padres del archivo: lo que se muestra y confirma en el paso 3. */
    poligonosPadre: Polygon[];
    /** Cuántos lotes quedaron guardados (los polígonos del archivo). */
    lotesGuardados: number;
    /** Qué se descartó al depurar, o `null` si no se descartó nada. */
    avisoDepuracion: string | null;
  }> {
    try {
      const response = await this._client().importarArchivoKml({
        registroId,
        blobUrl,
        nombreArchivo,
      });
      return {
        poligonosPadre: response.poligonosPadre.map(wkbToPolygon),
        lotesGuardados: response.lotesGuardados,
        avisoDepuracion: response.avisoDepuracion || null,
      };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 3, modo Editar: manda UNA operación sobre UN vértice de una pieza
   *  padre y devuelve la geometría nueva de las piezas que cambiaron (una,
   *  salvo que el vértice lo compartan dos piezas que se tocan).
   *
   *  **Lo que viaja es un punto.** Antes el cliente aplicaba el vértice en su
   *  memoria y mandaba `GuardarAoi` con la geometría COMPLETA de todas las
   *  piezas —con un área importada de un KML, cientos de kB por cada vértice
   *  que se suelta— más un POST WFS con el polígono entero en GML solo para
   *  preguntarle a PostGIS si se solapaba. El servidor ahora aplica la
   *  operación sobre la geometría guardada, valida la forma (cruzada sobre sí
   *  misma o pisando otra pieza) dentro de la misma transacción y, si no pasa,
   *  no toca nada y responde `VERTICE_INVALIDO`.
   *
   *  **No guarda el área.** El perímetro lo recalcula el servidor en el
   *  `guardarAoiAsync` que sale al terminar de editar ("Continuar" o "Volver"),
   *  no una vez por vértice. */
  async editarVerticePoligonoPadreAsync(
    registroId: string,
    operacion: {
      /** La pieza a editar. Solo hace falta al insertar: al mover y al quitar,
       *  el vértice se nombra por sus coordenadas. */
      poligonoPadreId?: string;
      origen?: Position;
      destino?: Position;
      indiceArista?: number;
    },
  ): Promise<{ id: string; polygon: Polygon }[]> {
    try {
      const response = await this._client().editarVerticePoligonoPadre({
        registroId,
        poligonoPadreId: operacion.poligonoPadreId,
        origen: operacion.origen ? pointToWkb(operacion.origen) : undefined,
        destino: operacion.destino ? pointToWkb(operacion.destino) : undefined,
        indiceArista: operacion.indiceArista,
      });
      return response.piezas.map((pieza) => ({
        id: pieza.id,
        polygon: wkbToPolygon(pieza.geometria),
      }));
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4, modo Dibujar: ¿el trazo que va se puede autocerrar contra un
   *  borde que ya existe? No crea nada.
   *
   *  **Se pregunta al poner un punto sobre un vértice conocido.** Antes el
   *  cliente daba el autocierre por hecho ahí mismo y mandaba `dibujarLote`:
   *  si esos dos vértices no estaban sobre un mismo contorno, o lo que cerraban
   *  pisaba otro lote, se perdía el trazo entero y salía un error. Ahora, si la
   *  respuesta es válida se confirma con un modal; si no, no pasa nada y se
   *  sigue dibujando.
   *
   *  `areaHas` son las hectáreas que tendría el lote: es lo único que le dice
   *  al usuario QUÉ va a crear antes de aceptar. */
  async validarAutocierreLoteAsync(
    registroId: string,
    trazo: Position[],
  ): Promise<{ valido: boolean; motivo: string; areaHas: number }> {
    try {
      const response = await this._client().validarAutocierreLote({
        registroId,
        trazo: lineStringToWkb(trazo),
      });
      return {
        valido: response.valido,
        motivo: response.motivo,
        areaHas: response.areaHas,
      };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 2. `datosDescartados`: el cambio de fuente borró el AOI y los
   *  lotes guardados. */
  async guardarFuenteAsync(
    registroId: string,
    aoiOrigen: "draw" | "kml",
  ): Promise<{ datosDescartados: boolean }> {
    try {
      return await this._client().guardarFuente({
        registroId,
        fuente: FUENTE_POR_ORIGEN[aoiOrigen],
      });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Pasos 2, 3 y 4: guarda las PIEZAS del área. **El perímetro lo calcula el
   *  servidor** a partir de ellas (`PerimetroGlobalGeometria`, la unión con
   *  puentes entre bloques separados): el cliente ya no lo arma ni lo manda.
   *
   *  **Por qué se movió.** Era el último trabajo pesado que quedaba en el
   *  navegador. Con un área importada de un KML, unir decenas de piezas tardaba
   *  segundos y bloqueaba el hilo principal justo en el "Continuar" del paso 3,
   *  que espera el guardado: la pantalla se congelaba en el cambio de paso.
   *
   *  Se llama al importar un archivo (paso 2), en cada polígono terminado,
   *  editado o borrado (paso 3) y cuando el paso 4 recalcula los padres desde
   *  los lotes. **Sin piezas** el registro queda sin área y sin lotes.
   *
   *  `areaCambio`: el área es distinta de la guardada (el avance volvió al paso
   *  2); `lotesEliminados`: además borró los lotes. */
  async guardarAoiAsync(
    registroId: string,
    /** Las piezas del área, SIN unir. Son lo único que viaja: el perímetro sale
     *  de ellas del lado del servidor. Se guardan además para poder
     *  restaurarlas al retomar el paso 3, porque unir es irreversible. */
    poligonosPadre: readonly Polygon[],
    /** `true` = no bajar el avance del registro aunque el área cambie. Lo usa
     *  el paso 4, donde el área se recalcula A PARTIR de los lotes: están
     *  alineados por construcción y no hay nada que volver a confirmar. */
    conservarPaso = false,
    /** El id que el cliente ya tiene para cada pieza, en la MISMA posición que
     *  `poligonosPadre`. Cadena vacía = pieza nueva.
     *
     *  **Mandarlos es lo que mantiene el id estable mientras se editan los
     *  vértices.** Sin ellos el servidor no puede distinguir una pieza que
     *  cambió de forma de una pieza nueva, así que cada vértice movido le
     *  estrenaría id — y el mapa se quedaría pidiéndole a GeoServer los
     *  vértices de una fila que acaba de quedar eliminada, con el modo Editar
     *  abierto justo sobre esa pieza. */
    poligonosPadreIds: readonly string[] = [],
  ): Promise<{
    lotesEliminados: boolean;
    areaCambio: boolean;
    /** El id de cada pieza guardada, en el orden en que se mandaron. El mapa
     *  los adopta: son los que usa para pedirle a GeoServer los vértices de la
     *  pieza que se está editando. */
    poligonosPadreIds: string[];
  }> {
    try {
      return await this._client().guardarAoi({
        registroId,
        // El campo sigue en el contrato pero el servidor lo IGNORA: el
        // perímetro lo calcula él con las piezas. Se manda vacío para no
        // serializar al pedo una geometría que nadie lee.
        aoiSatelite: new Uint8Array(),
        poligonosPadre: poligonosPadre.map(polygonToWkb),
        conservarPaso,
        poligonosPadreIds: [...poligonosPadreIds],
      });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4: parte UN lote por la línea de corte que el usuario trazó.
   *
   *  **El corte lo hace el servidor.** Lo que viaja es la operación —qué lote y
   *  por dónde—, no el resultado: antes el navegador partía el polígono con
   *  turf y mandaba las dos mitades por `ReemplazarLotes`, y el servidor no
   *  tenía forma de verificar lo que estaba guardando.
   *
   *  La línea va TAL CUAL se dibujó, sin prolongar: los 10 cm de extensión de
   *  cada extremo —lo que hace que la línea cruce el borde de verdad cuando el
   *  snap la dejó a milímetros por dentro— los agrega el servidor, que es donde
   *  vive esa regla.
   *
   *  Devuelve los lotes del registro COMPLETOS: el corte reemplaza uno por dos
   *  y además puede insertarle un vértice al borde de un vecino, así que el
   *  cliente se queda con lo que hay en la base y no con su memoria más un
   *  parche. `mitadesIds` son las dos piezas que salieron del corte, para poder
   *  volver a emparejarlas en el atajo de "fusionar de vuelta". */
  async dividirLoteAsync(
    registroId: string,
    loteId: string,
    linea: readonly Position[],
  ): Promise<{ lotes: RegistroFincaBorradorLote[]; mitadesIds: string[] }> {
    try {
      const respuesta = await this._client().dividirLote({
        registroId,
        loteId,
        linea: lineStringToWkb(linea),
      });
      return {
        lotes: respuesta.lotes.map(aLoteBorrador),
        mitadesIds: [...respuesta.mitadesIds],
      };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4: crea un lote con el trazo que el usuario dibujó.
   *
   *  **La geometría final la decide el servidor**, igual que el corte y la
   *  unión. El cliente manda los puntos que se clickearon, no un lote armado:
   *  antes el navegador cerraba el anillo, lo validaba con turf
   *  (`LoteSplitState.agregarLote`, la última validación geométrica que quedaba
   *  acá) y lo mandaba por `ReemplazarLotes` como si fuera un lote cualquiera.
   *
   *  **El trazo puede ir ABIERTO.** Si empieza sobre un vértice de un polígono
   *  padre y termina sobre otro del mismo padre, el servidor completa el
   *  contorno con el pedazo de borde que falta, leído de la base. Así el borde
   *  compartido queda exacto —son las mismas coordenadas del padre— y el
   *  usuario no tiene que clickear vértice por vértice todo ese borde. Un trazo
   *  abierto que no se puede cerrar se rechaza con `TRAZO_ABIERTO`.
   *
   *  Devuelve los lotes del registro COMPLETOS, por lo mismo que
   *  `dividirLoteAsync`. `autocerrado` dice si el contorno se completó con el
   *  borde del padre, para poder avisarlo. */
  async dibujarLoteAsync(
    registroId: string,
    trazo: readonly Position[],
  ): Promise<{
    lotes: RegistroFincaBorradorLote[];
    loteNuevoId: string;
    autocerrado: boolean;
  }> {
    try {
      const respuesta = await this._client().dibujarLote({
        registroId,
        trazo: lineStringToWkb(trazo),
      });
      return {
        lotes: respuesta.lotes.map(aLoteBorrador),
        loteNuevoId: respuesta.loteNuevoId,
        autocerrado: respuesta.autocerrado,
      };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4: une DOS lotes adyacentes en uno.
   *
   *  **La adyacencia la decide el servidor**, igual que el corte. El cliente
   *  manda qué dos lotes quiere unir, no la geometría resultante: antes el
   *  navegador medía el borde compartido con turf, hacía la unión y mandaba la
   *  pieza ya unida, y el servidor no tenía forma de verificar nada de eso.
   *
   *  Si no comparten un TRAMO de borde, el servidor rechaza con
   *  `LOTES_NO_ADYACENTES` y el mensaje nombra los dos lotes.
   *
   *  Devuelve los lotes del registro COMPLETOS, por lo mismo que
   *  `dividirLoteAsync`: el cliente se queda con lo que hay en la base.
   *  `loteUnidoId` es la pieza que resultó. */
  async unirLotesAsync(
    registroId: string,
    loteIdA: string,
    loteIdB: string,
  ): Promise<{ lotes: RegistroFincaBorradorLote[]; loteUnidoId: string }> {
    try {
      const respuesta = await this._client().unirLotes({
        registroId,
        loteIdA,
        loteIdB,
      });
      return {
        lotes: respuesta.lotes.map(aLoteBorrador),
        loteUnidoId: respuesta.loteUnidoId,
      };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4: mueve, inserta o quita UN vértice de los lotes.
   *
   *  **Es topológico y lo resuelve el servidor.** El vértice se toca en todos
   *  los lotes que lo comparten, no sólo en el que el usuario agarró: si no,
   *  entre los dos queda una rendija que el guardado final rechaza por solape.
   *  Y la validación —que ninguna forma quede cruzada sobre sí misma ni pisando
   *  a un vecino— corre allá: antes la hacía el navegador con turf y el servidor
   *  recibía la geometría ya cambiada, sin forma de verificarla.
   *
   *  Las tres formas se distinguen por los argumentos:
   *   - **mover**: `origen` y `destino`;
   *   - **quitar**: sólo `origen`;
   *   - **insertar**: `insercion` (lote y arista) y `destino`. Es una sola
   *     operación porque así la vive el usuario: agarra el punto medio de un
   *     tramo y lo arrastra.
   *
   *  Si el servidor rechaza, NO tocó nada: el cliente recarga los lotes de la
   *  base y queda como estaba. */
  async editarVerticeLoteAsync(
    registroId: string,
    operacion: {
      origen?: Position;
      destino?: Position;
      insercion?: { loteId: string; indiceArista: number };
    },
  ): Promise<{ lotes: RegistroFincaBorradorLote[] }> {
    try {
      const respuesta = await this._client().editarVerticeLote({
        registroId,
        origen: operacion.origen ? pointToWkb(operacion.origen) : undefined,
        destino: operacion.destino ? pointToWkb(operacion.destino) : undefined,
        loteId: operacion.insercion?.loteId,
        indiceArista: operacion.insercion?.indiceArista,
      });
      return { lotes: respuesta.lotes.map(aLoteBorrador) };
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 3 ("Continuar"): alinea los lotes de la base con las piezas del
   *  mapa. El servidor conserva los lotes de una pieza sin modificar que
   *  ya estaba subdividida (la cubren en más del 99%), borra los de una
   *  pieza modificada y agrega como lote cada pieza que no estaba
   *  subdividida. */
  async confirmarPiezasAsync(
    registroId: string,
    piezas: { polygon: Polygon; nombre: string | null; modificada: boolean }[],
  ): Promise<void> {
    try {
      await this._client().confirmarPiezas({
        registroId,
        piezas: piezas.map((pieza) => ({
          geometria: polygonToWkb(pieza.polygon),
          nombre: pieza.nombre ?? undefined,
          modificada: pieza.modificada,
        })),
      });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Reemplaza todos los lotes. Devuelve los ids en el mismo orden. El
   *  servidor rechaza (`LOTE_SOLAPADO`) lotes que se solapen entre sí. */
  async iniciarLotesAsync(
    registroId: string,
    lotes: LoteTempNuevo[],
  ): Promise<string[]> {
    try {
      const response = await this._client().iniciarLotes({
        registroId,
        lotes: lotes.map(aLoteNuevo),
      });
      return response.lotes.map((lote) => lote.id);
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 4 (dividir/unir): borra por id, inserta los nuevos y ajusta la
   *  geometría de los vecinos. Devuelve los ids de los insertados, en orden. */
  async reemplazarLotesAsync(
    registroId: string,
    cambio: {
      eliminarIds: string[];
      agregar: LoteTempNuevo[];
      actualizarGeometria: { id: string; polygon: Polygon }[];
    },
  ): Promise<string[]> {
    try {
      const response = await this._client().reemplazarLotes({
        registroId,
        eliminarIds: cambio.eliminarIds,
        agregar: cambio.agregar.map(aLoteNuevo),
        actualizarGeometria: cambio.actualizarGeometria.map((vecino) => ({
          id: vecino.id,
          geometria: polygonToWkb(vecino.polygon),
        })),
      });
      return response.lotes.map((lote) => lote.id);
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Pasos 4 a 6: nombre y/o asignación de lotes ya guardados. */
  async actualizarLotesAsync(
    registroId: string,
    actualizaciones: ActualizacionLoteTemp[],
  ): Promise<void> {
    if (actualizaciones.length === 0) return;
    try {
      await this._client().actualizarLotes({
        registroId,
        lotes: actualizaciones.map((a) => ({
          id: a.id,
          nombre: a.nombreLote,
          asignacion: a.asignacion
            ? {
                areaProductiva: a.asignacion.isAreaProductiva ?? undefined,
                cultivoId: a.asignacion.cultivoId ?? undefined,
                coberturaId: a.asignacion.coberturaId ?? undefined,
              }
            : undefined,
        })),
      });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Marca un paso (1 a 5) como confirmado. */
  async avanzarPasoAsync(registroId: string, paso: number): Promise<void> {
    try {
      await this._client().avanzarPaso({ registroId, paso });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }

  /** Paso 6: vuelca el registro a las fincas reales y deja la finca
   *  resultante en la caché local. */
  async finalizarAsync(registroId: string): Promise<{ id: string }> {
    let response: GuardarResponse;
    try {
      response = await this._client().finalizar({ registroId });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
    await guardarFincaEnCacheAsync(response);
    return { id: response.id };
  }

  /** Descarta el registro en curso. */
  async descartarAsync(registroId: string): Promise<void> {
    try {
      await this._client().descartar({ registroId });
    } catch (error) {
      throw mapFincaWizardGrpcError(error);
    }
  }
}

/** Instancia única del servicio del registro temporal. */
export const registroFincaTempGrpcService = new RegistroFincaTempGrpcService();

/** El registro temporal del servidor en el shape del cliente. */
export function fx_leerRegistroTemporal(
  registro: RegistroTemporal,
  clienteId: string,
): RegistroTemporalLeido {
  return {
    borrador: {
      clienteId,
      registroId: registro.id,
      ultimoPaso: registro.ultimoPaso,
      nombre: registro.nombre,
      tipo: registro.tipo,
      aoiOrigen:
        registro.fuente === FUENTE_POR_ORIGEN.kml
          ? "kml"
          : registro.fuente === FUENTE_POR_ORIGEN.draw
            ? "draw"
            : null,
      tieneArea: !!registro.aoiSatelite,
      fincaIdACompletar: registro.fincaId ?? null,
      esEdicion: registro.esEdicion,
    },
    aoi: registro.aoiSatelite ? wkbToPolygon(registro.aoiSatelite) : null,
    poligonosPadre: registro.poligonosPadre.map(wkbToPolygon),
    poligonosPadreIds: [...registro.poligonosPadreIds],
    lotes: registro.lotes.map(aLoteBorrador),
    archivoNombre: registro.archivoNombre ?? null,
    archivoAvisoDepuracion: registro.archivoAvisoDepuracion ?? null,
  };
}

/** Deja en `grpcFincaLoteDatabase` la finca que devuelve `Finalizar` **con sus
 *  lotes**, así `ClienteAsideMenuRender` la ve sin ningún refresco aparte, y
 *  vacía la caché de lotes de esa finca (`grpcLoteDatabase`, la del delta por
 *  finca — otra base) para que se pidan de nuevo.
 *
 *  Los lotes NO son opcionales acá: `_esCompletaAsync` (`ClienteAsideMenuRender`)
 *  pide AOI **y** al menos un lote cacheado para dar una finca por completa, y
 *  ese store solo lo llenaba el login. Sin esto, la finca recién creada por el
 *  wizard quedaba clasificada como incompleta apenas terminaba el paso 6: el
 *  sidebar mostraba "Terminar registro de …" y ese recordatorio reabría el
 *  wizard en modo completar (paso 2), que el server rechazaba con
 *  `FINCA_YA_COMPLETA` porque la finca ya tenía su AOI. */
async function guardarFincaEnCacheAsync(response: GuardarResponse): Promise<void> {
  await grpcFincaLoteDatabase.upsertFincaAsync({
    id: response.id,
    nombreFinca: response.nombreFinca,
    hectareasFinca: response.hectareasFinca,
    hectareasProductivas: response.hectareasProductivas,
    tipoFinca: response.tipoFinca,
    clienteId: response.clienteId,
    departamentoId: response.departamentoId,
    departamentoNombre: response.departamentoNombre,
    municipioId: response.municipioId,
    municipioNombre: response.municipioNombre,
    departamentoRegionId: response.departamentoRegionId,
    departamentoRegionNombre: response.departamentoRegionNombre,
    ganaderiaCrecimientoDiarioGr: response.ganaderiaCrecimientoDiarioGr,
    ganaderiaDiasDescansoOptimo: response.ganaderiaDiasDescansoOptimo,
    ganaderiaDiasOcupacionOptimo: response.ganaderiaDiasOcupacionOptimo,
    ganaderiaAnimalesXHectareaOptimo: response.ganaderiaAnimalesXHectareaOptimo,
    arbolesMonitorear: response.arbolesMonitorear,
    uriOrtomosaico: response.uriOrtomosaico,
    conteoPreRender: response.conteoPreRender,
    pasoRegistroCompletado: response.pasoRegistroCompletado,
    ubicacion: response.ubicacion ? wkbToPoint(response.ubicacion) : null,
    sateliteAreaGeografica: response.sateliteAreaGeografica
      ? wkbToPolygon(response.sateliteAreaGeografica)
      : null,
  });
  await grpcFincaLoteDatabase.replaceLotesDeFincaAsync(
    response.id,
    response.lotes.map((lote) => ({
      id: lote.id,
      fincaId: lote.fincaId,
      nombreLote: lote.nombreLote,
      areaHectareas: lote.areaHectareas,
      areaCuadras: lote.areaCuadras,
      isAreaProductiva: lote.isAreaProductiva,
      areaGeografica: wkbToPolygon(lote.areaGeografica),
      cultivoId: lote.cultivoId,
      cultivoNombre: lote.cultivoNombre,
    })),
  );
  // La finca nace (o cambia) con sus lotes: recarga completa de sus lotes, no el delta (mismo criterio que
  // el resto de los cambios que afectan lotes, ver CLAUDE.md, sección IndexedDB).
  await fx_createLoteGrpcService(response.id).recargarTodoAsync();
}

function aLoteBorrador(lote: LoteTemporal): RegistroFincaBorradorLote {
  return {
    id: lote.id,
    nombreLote: lote.nombre,
    areaGeograficaPolygon: wkbToPolygon(lote.geometria),
    loteOrigenId: null,
    isAreaProductiva: lote.areaProductiva ?? null,
    cultivoId: lote.cultivoId ?? null,
    coberturaId: lote.coberturaId ?? null,
  };
}

function aLoteNuevo(lote: LoteTempNuevo) {
  return {
    nombre: lote.nombreLote,
    geometria: polygonToWkb(lote.areaGeograficaPolygon),
    areaProductiva: lote.isAreaProductiva ?? undefined,
    cultivoId: lote.cultivoId ?? undefined,
    coberturaId: lote.coberturaId ?? undefined,
  };
}
